import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import * as KEYCODE from '../../constants/keycode';

import Hidden from '../layouts/components/hidden';

import PlayPauseButton from './play-pause-button';
import ProgressControl from './progress-bar';
import StopButton from './stop-button';
import VolumeButton from './volume-button';
import Fallback from '../fallback';
import { getTimeString } from '../../lib/utils';

const PlayerContainer = styled.div`
    display: flex;
    flex-grow: 1;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    border-radius: 4px;
    box-shadow: var(--shadow-4);
    height: 48px;
    background: var(--color-white);

    &:hover {
        box-shadow: var(--shadow-6);
    }
`;

export const PlayerText = styled.p`
    color: var(--gray700);
    font-size: 12px;
    font-weight: 600;
    height: 16px;
    letter-spacing: 0.32px;
    line-height: 16px;
    margin: 0;
    align-self: center;
`;

const Actions = styled.div`
    display: flex;
    width: 100%;
    padding: 0 16px;
`;

const ProgressTimeContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    margin: 0;

    ${(props) => props.theme.mediaQueries.tablet} {
        margin: 0 16px;
    }
`;

const VolumeBarContainer = styled.div`
    display: flex;
    align-items: center;
    min-width: 64px;
    width: 64px;
    margin-right: 16px;
`;

const ButtonStopWrapper = styled.div`
    align-self: center;
    margin: 0 16px;
`;

const ButtonVolumeWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const TimeControlsContainer = styled.div`
    display: flex;
    flex-grow: 1;
    flex-direction: column;

    ${(props) => props.theme.mediaQueries.tablet} {
        flex-direction: row;
    }
`;

const MobileTextContainer = styled.div`
    display: flex;
    flex-grow: 1;
    justify-content: space-between;
`;

export const playPauseAudio = (audio, running, setRunning) => {
    setRunning(!running);
    if (audio.paused) {
        audio.play();
    } else {
        audio.pause();
    }
};

export const stop = (audio, setRunning) => {
    audio.pause();
    audio.currentTime = 0;
    setRunning(false);
};

export const mute = (audio, muted, volume, setMuted, setVolume) => {
    if (!muted && volume === 0) {
        audio.volume = 0.5;
        setVolume(50);
        return;
    }

    audio.muted = !muted;
    setMuted(!muted);
};

const AudioPlayer = ({ source }) => {
    const audioRef = useRef(null);

    const [running, setRunning] = useState(false);
    const [current, setCurrent] = useState(null);
    const [duration, setDuration] = useState(-1);
    const [volume, setVolume] = useState(50);
    const [muted, setMuted] = useState(false);

    const onChangeTimeline = (progress) => {
        const { current: audio } = audioRef;
        audio.currentTime = (duration * progress) / 100;
    };

    const onChangeVolume = (progress) => {
        const { current: audio } = audioRef;
        audio.volume = progress / 100;
        setVolume(Math.floor(progress));
        if (muted) {
            audio.muted = false;
            setMuted(false);
        }
    };

    const keyboardHandler = (event) => {
        event.stopPropagation();

        const stepInSeconds = duration / 100;

        if (event.keyCode === KEYCODE.LEFT_ARROW) {
            const currentShifted = current - stepInSeconds < 0 ? 0 : current - stepInSeconds;
            setCurrent(currentShifted);
        } else if (event.keyCode === KEYCODE.RIGHT_ARROW) {
            const currentShifted = current + stepInSeconds > duration ? duration : current + stepInSeconds;
            setCurrent(currentShifted);
        }
    };

    return (
        <Fallback
            height={48}
            as="audio"
            src={source}
            text="C'è un problema con l’audio. Ricarica la pagina o contattaci."
            id="audio-player"
            controls={false}
            ref={audioRef}
            preload="metadata"
            onLoadedMetadata={(event) => setDuration(event.target.duration)}
            onTimeUpdate={(event) => setCurrent(event.target.currentTime)}
            onEnded={() => stop(audioRef.current, setRunning)}
        >
            <PlayerContainer aria-label="Audio Player">
                <PlayPauseButton
                    ariaControls="audio-player"
                    ariaLabel={running ? 'Pause' : 'Play'}
                    onClick={() => playPauseAudio(audioRef.current, running, setRunning)}
                    running={running}
                />

                <Actions>
                    <TimeControlsContainer>
                        <Hidden below="tablet">
                            <PlayerText>{getTimeString(current)}</PlayerText>
                        </Hidden>

                        <ProgressTimeContainer>
                            <ProgressControl
                                ariaControls="audio-player"
                                ariaLabel="Timeline Bar"
                                keyboardHandler={keyboardHandler}
                                progress={(current / duration) * 100}
                                onChange={onChangeTimeline}
                            />
                        </ProgressTimeContainer>

                        <Hidden above="tablet">
                            <MobileTextContainer>
                                <PlayerText>{getTimeString(current)}</PlayerText>
                                <PlayerText>{getTimeString(duration)}</PlayerText>
                            </MobileTextContainer>
                        </Hidden>

                        <Hidden below="tablet">
                            <PlayerText>{getTimeString(duration)}</PlayerText>
                        </Hidden>
                    </TimeControlsContainer>

                    <ButtonStopWrapper>
                        <StopButton ariaControls="audio-player" ariaLabel="Stop" onClick={() => stop(audioRef.current, setRunning)} />
                    </ButtonStopWrapper>

                    <Hidden below="tablet">
                        <VolumeBarContainer>
                            <ProgressControl
                                ariaControls="audio-player"
                                ariaLabel="Volume Bar"
                                onChange={onChangeVolume}
                                progress={muted ? 0 : volume}
                            />
                        </VolumeBarContainer>
                    </Hidden>
                    <ButtonVolumeWrapper>
                        <VolumeButton
                            ariaControls="audio-player"
                            ariaLabel={volume !== 0 && !muted ? 'Mute' : 'Volume Enable'}
                            onClick={() => mute(audioRef.current, muted, volume, setMuted, setVolume)}
                            hasVolume={volume !== 0 && !muted}
                        />
                    </ButtonVolumeWrapper>
                </Actions>
            </PlayerContainer>
        </Fallback>
    );
};

AudioPlayer.propTypes = {
    source: PropTypes.string.isRequired,
};

export default AudioPlayer;
